import { useState } from "react";
import { Badge, Checkbox, Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/shadcn";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { ChevronRight, X } from "lucide-react";
import type { TProject } from "@/zapi-frontend/stores/project";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import type { TActiveFilters } from "../lib/portfolioStorage";
import { distinctPeopleValues, distinctValuesForDimension } from "../lib/usePortfolioDerivedList";
import {
  STATUS_LABEL_KEYS,
  EMPTY_VALUE_TOKEN,
  FILTER_CHECKBOX_CLASS,
  OWNER_FILTER_KEY,
  STATUS_DOT_COLORS,
  STATUS_FILTER_KEY,
  STATUS_OPTIONS,
  type TPortfolioStatus
} from "../const/const";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { FilterChipButton } from "./ProjectListV2/_FilterChipButton";
import { useTranslation } from "react-i18next";

const MAX_VISIBLE_DIMENSIONS = 4;

type TProps = {
  dimensions: TOrganizationProjectDimension[];
  projects: TProject[];
  filters: TActiveFilters;
  onToggleFilterValue: (dimId: string, value: string, checked: boolean) => void;
  onClearAll: () => void;
};

export function DimensionChipsRow({ dimensions, projects, filters, onToggleFilterValue, onClearAll }: TProps) {
  const { t } = useTranslation("projects");
  const statusSelected = filters[STATUS_FILTER_KEY] ?? new Set<string>();
  const creatorSelected = filters[OWNER_FILTER_KEY] ?? new Set<string>();
  const creatorValues = distinctPeopleValues(projects, "createdBy");
  const activeCount = Object.values(filters).reduce((n, s) => n + s.size, 0);

  const visible = dimensions.slice(0, MAX_VISIBLE_DIMENSIONS);
  const overflow = dimensions.slice(MAX_VISIBLE_DIMENSIONS);

  return (
    <>
      <StatusChip
        selected={statusSelected}
        onToggle={(value, checked) => onToggleFilterValue(STATUS_FILTER_KEY, value, checked)}
      />
      {creatorValues.length > 0 && (
        <ValuesChip
          name="Creator"
          label={t("card.creator")}
          values={creatorValues}
          selected={creatorSelected}
          onToggle={(value, checked) => onToggleFilterValue(OWNER_FILTER_KEY, value, checked)}
        />
      )}
      {visible.map((d) => {
        const dimId = String(d.id);
        const values = distinctValuesForDimension(projects, dimId, d);
        const selected = filters[dimId] ?? new Set<string>();
        return (
          <ValuesChip
            key={d.id}
            name={d.name}
            values={values}
            selected={selected}
            onToggle={(value, checked) => onToggleFilterValue(dimId, value, checked)}
          />
        );
      })}
      {overflow.length > 0 && (
        <MoreChip
          dimensions={overflow}
          projects={projects}
          filters={filters}
          onToggleFilterValue={onToggleFilterValue}
        />
      )}
      {activeCount > 0 && (
        <button
          type="button"
          onClick={onClearAll}
          data-testid="filters-clear-all"
          className="ml-1 flex h-8 shrink-0 items-center gap-1 rounded-md px-2 text-[14px] font-medium text-zinc-700 hover:bg-zinc-50"
        >
          <X className="h-3.5 w-3.5 shrink-0 text-zinc-500" strokeWidth={1.5} />
          {t("filters.clearAll")}
        </button>
      )}
    </>
  );
}

type TMoreProps = {
  dimensions: TOrganizationProjectDimension[];
  projects: TProject[];
  filters: TActiveFilters;
  onToggleFilterValue: (dimId: string, value: string, checked: boolean) => void;
};

function MoreChip({ dimensions, projects, filters, onToggleFilterValue }: TMoreProps) {
  const { t } = useTranslation("projects");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const activeOverflow = dimensions.reduce((n, d) => n + (filters[String(d.id)]?.size ?? 0), 0);

  const toggle = (dimId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(dimId)) next.delete(dimId);
      else next.add(dimId);
      return next;
    });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <FilterChipButton state={activeOverflow > 0 ? "selected" : "default"} count={activeOverflow} withCaret>
          {t("filters.more")}
        </FilterChipButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[260px] max-h-[420px] overflow-y-auto rounded-lg border border-zinc-100 bg-white p-1 shadow-md"
      >
        {dimensions.map((d) => {
          const dimId = String(d.id);
          const values = distinctValuesForDimension(projects, dimId, d);
          const selected = filters[dimId] ?? new Set<string>();
          const isOpen = expanded.has(dimId);
          return (
            <div key={d.id} className="border-b border-zinc-100 last:border-0">
              <button
                type="button"
                onClick={() => toggle(dimId)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-[14px] font-medium text-zinc-700 hover:bg-zinc-50"
              >
                <ChevronRight
                  className={cn("h-3.5 w-3.5 shrink-0 text-zinc-500 transition-transform", isOpen && "rotate-90")}
                  strokeWidth={1.5}
                />
                <span className="min-w-0 flex-1 truncate">{d.name}</span>
                {selected.size > 0 && (
                  <Badge
                    variant="secondary"
                    className="border-0 bg-zinc-900 px-1.5 py-0 text-[10px] font-medium text-white hover:bg-zinc-900"
                  >
                    {selected.size}
                  </Badge>
                )}
              </button>
              {isOpen && (
                <div className="pb-1 pl-7 pr-2">
                  {values.length === 0 ? (
                    <FigmaTypography component="div" variant="body-3" className="px-2 py-1 text-zinc-500">
                      {t("filters.noValues")}
                    </FigmaTypography>
                  ) : (
                    values.map((v) => (
                      <label
                        key={v}
                        className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[14px] hover:bg-zinc-50"
                      >
                        <Checkbox
                          checked={selected.has(v)}
                          onCheckedChange={(c) => onToggleFilterValue(dimId, v, c === true)}
                          className={FILTER_CHECKBOX_CLASS}
                        />
                        {v === EMPTY_VALUE_TOKEN ? (
                          <span className="italic text-zinc-500">{t("filters.notSet")}</span>
                        ) : (
                          <span className="truncate">{v}</span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

type TStatusProps = {
  selected: Set<string>;
  onToggle: (value: string, checked: boolean) => void;
};

function StatusChip({ selected, onToggle }: TStatusProps) {
  const { t } = useTranslation("projects");
  const options: readonly string[] = STATUS_OPTIONS;
  const isActive = selected.size > 0;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <FilterChipButton state={isActive ? "selected" : "default"} count={selected.size} withCaret>
          <span className="max-w-[220px] truncate">{t("card.status")}</span>
        </FilterChipButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[220px] rounded-lg border border-zinc-100 bg-white p-1 shadow-md"
      >
        <div className="flex flex-col">
          {options.map((status) => {
            const checked = selected.has(status);
            return (
              <label
                key={status}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[14px] hover:bg-zinc-50"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(c) => onToggle(status, c === true)}
                  className={FILTER_CHECKBOX_CLASS}
                />
                <span className="flex items-center gap-1.5">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: STATUS_DOT_COLORS[status as TPortfolioStatus] }}
                  />
                  {t(STATUS_LABEL_KEYS[status as TPortfolioStatus])}
                </span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

type TValuesChipProps = {
  name: string;
  label?: string;
  values: string[];
  selected: Set<string>;
  onToggle: (value: string, checked: boolean) => void;
};

function ValuesChip({ name, label, values, selected, onToggle }: TValuesChipProps) {
  const { t } = useTranslation("projects");
  const isActive = selected.size > 0;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <FilterChipButton state={isActive ? "selected" : "default"} count={selected.size} withCaret>
          <span className="max-w-[260px] truncate">{label ?? name}</span>
        </FilterChipButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[240px] max-h-[320px] overflow-y-auto rounded-lg border border-zinc-100 bg-white p-1 shadow-md"
      >
        {values.length === 0 ? (
          <div className="px-3 py-4 text-center text-[14px] text-zinc-500">{t("filters.noValues")}</div>
        ) : (
          <div className="flex flex-col">
            {values.map((v) => (
              <label
                key={v}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[14px] hover:bg-zinc-50"
              >
                <Checkbox
                  checked={selected.has(v)}
                  onCheckedChange={(c) => onToggle(v, c === true)}
                  className={FILTER_CHECKBOX_CLASS}
                />
                {v === EMPTY_VALUE_TOKEN ? (
                  <span className="italic text-zinc-500">{t("filters.notSet")}</span>
                ) : (
                  <span>{v}</span>
                )}
              </label>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
